import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineSparkles,
} from "react-icons/hi2";
import { TbHome, TbFlask2 } from "react-icons/tb";
import BotFace from "../../design-system/icons/BotFace.jsx";

const SPOT_ROOT = (process.env.REACT_APP_SPOT_URL || "https://stg2.xenorize.com/dashboard").replace(/\/dashboard$/, "");

const NAV_ITEMS = [
  { label: "Dashboard",       icon: TbHome,   to: "/" },
  { label: "Bot",             icon: BotFace,  to: "/bot" },
  { label: "Forward Testing", icon: TbFlask2, to: "/forward-testing" },
];

function daysLeft(periodEnd) {
  if (!periodEnd) return 0;
  const diff = Math.ceil((periodEnd - Date.now() / 1000) / 86400);
  return Math.max(0, diff);
}

function SubscriptionCard({ sub, collapsed }) {
  const isTrial = !sub?.is_active || sub?.product_name === "Free Trial" || !sub?.product_name;
  const days = daysLeft(sub?.period_end);
  const trialDays = 14;
  const pct = isTrial
    ? Math.max(4, Math.min(100, ((trialDays - days) / trialDays) * 100))
    : 100;

  if (collapsed) {
    return (
      <div className="flex justify-center pb-5">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] flex items-center justify-center">
          <HiOutlineSparkles className="w-4 h-4 text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-3 mb-4 rounded-xl bg-[#EEF2FF] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] flex items-center justify-center shrink-0">
            <HiOutlineSparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs font-semibold text-[#21272A]">
            {isTrial ? "Your Free Trial" : (sub?.product_name || "Pro")}
          </span>
        </div>
        {isTrial && (
          <span className="text-[10px] font-medium text-[#5B3DF6]">
            {days}d left
          </span>
        )}
      </div>

      {isTrial && (
        <>
          <div className="w-full h-1.5 bg-[#C7D2FE] rounded-full mb-2">
            <div
              className="h-1.5 rounded-full bg-[#5B3DF6] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] text-[#697077] mb-2">
            Upgrade to unlock other feature
          </p>
          <a
            href={`${SPOT_ROOT}/account?tab=subscription`}
            target="_blank"
            rel="noreferrer"
            className="block w-full text-center text-xs font-semibold text-white bg-[#1C1C1E] rounded-lg py-1.5 hover:bg-[#333] transition-colors"
          >
            Upgrade
          </a>
        </>
      )}

      {!isTrial && sub?.period_end && (
        <p className="text-[10px] text-[#697077]">
          Renews {new Date(sub.period_end * 1000).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })}
        </p>
      )}
    </div>
  );
}

export default function Sidebar({ user }) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar_collapsed") === "true"
  );

  const toggle = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar_collapsed", String(!prev));
      return !prev;
    });
  };

  return (
    <aside
      className={`hidden desktop:flex desktop:flex-col shrink-0 h-screen bg-white border-r border-gray-200 transition-all duration-200 overflow-hidden ${
        collapsed ? "w-16" : "w-56"
      }`}
    >
      {/* Header */}
      <div className={`flex items-center h-14 border-b border-gray-100 shrink-0 ${collapsed ? "justify-center px-2" : "px-3 gap-2"}`}>
        <img src="/xenorize-x.png" width="28" height="28" alt="Xenorize" className="shrink-0" />
        {!collapsed && (
          <span className="text-base font-bold text-[#21272A] tracking-tight flex-1">Xenorize</span>
        )}
        <button
          onClick={toggle}
          title={collapsed ? "Expand" : "Collapse"}
          className={`w-7 h-7 flex items-center justify-center rounded-lg text-[#697077] hover:bg-gray-100 transition-colors shrink-0 ${collapsed ? "mt-0" : ""}`}
        >
          {collapsed
            ? <HiOutlineChevronDoubleRight className="w-4 h-4" />
            : <HiOutlineChevronDoubleLeft className="w-4 h-4" />
          }
        </button>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-0.5 flex-1 px-2 py-3 overflow-y-auto">
        {NAV_ITEMS.map(({ label, icon: Icon, to }) =>
          to ? (
            <NavLink
              key={label}
              to={to}
              end
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl transition-colors h-10 px-2.5 w-full ${
                  collapsed ? "justify-center" : ""
                } ${
                  isActive
                    ? "bg-[#EFEAFE] text-[#5B3DF6]"
                    : "text-[#697077] hover:bg-gray-100"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 shrink-0 ${isActive ? "text-[#5B3DF6]" : ""}`} />
                  {!collapsed && (
                    <span className="text-sm font-medium whitespace-nowrap">{label}</span>
                  )}
                </>
              )}
            </NavLink>
          ) : (
            <button
              key={label}
              type="button"
              title={collapsed ? label : undefined}
              className={`flex items-center gap-3 rounded-xl h-10 px-2.5 text-[#697077] hover:bg-gray-100 transition-colors ${
                collapsed ? "justify-center" : ""
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium whitespace-nowrap">{label}</span>
              )}
            </button>
          )
        )}
      </nav>

      {/* Subscription */}
      <SubscriptionCard sub={user?.subscription} collapsed={collapsed} />
    </aside>
  );
}
